/**
 * "Showing the first N rows" — the one place a visual admits it is partial.
 *
 * Every visual runs under a row limit: the author's Top-N, or the instance
 * ceiling when they set none. Before this existed the limit was silent, so a
 * chart of the first 5000 of 40000 orders looked exactly like a chart of all
 * the orders anyone had — and the only reader who could tell was one who
 * already knew the number. A dashboard tile knew (it declined the quick
 * filter), but it never said why.
 *
 * So the badge is deliberately not an error style: nothing has gone wrong, the
 * reader simply needs to know what they are looking at. It appears ONLY when
 * the server saw another row past the limit, which it learns by asking for one
 * more than it needs rather than by comparing a count with the limit — those
 * two are the same number just as often when the data ends exactly there.
 *
 * Clicking it opens the rows control, which raises this viewer's own limit up
 * to the instance ceiling. That is a view preference, not an edit: it changes
 * nothing about the visual for anybody else. An author may pin the limit
 * (`rowLimitFixed`) — a Top-5 chart turned into a Top-500 by a reader is a
 * different visual, not more of the same one — and then the badge explains
 * itself and offers nothing.
 */
import { useEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'

export interface RowLimitBadgeProps {
  /** The server saw at least one row past the limit. */
  truncated?: boolean
  /** The limit that was applied to this run. */
  rowLimit?: number | null
  /** The most rows this instance allows one visual to return. */
  rowCeiling?: number | null
  /** The limit came from the author's Top-N rather than from the ceiling. */
  rowLimitAuthored?: boolean
  /** The author pinned the limit: no raising it. */
  rowLimitFixed?: boolean
  /**
   * Ask for more rows. The host re-fetches with this as the viewer's limit.
   * Absent = no control, badge only (a surface with no way to re-query, such
   * as a thumbnail, must not offer a button that does nothing).
   */
  onRowLimit?: (limit: number) => void
  /** Rendered inline in a header strip, or sticky at the top of a scroller. */
  variant?: 'inline' | 'sticky'
}

const CHIP: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0,
  fontSize: 11, lineHeight: 1.6, padding: '0 6px', borderRadius: 10,
  border: '1px solid var(--pg-warning, var(--color-warning, #b45309))',
  color: 'var(--pg-warning, var(--color-warning, #b45309))', background: 'transparent',
  cursor: 'default', whiteSpace: 'nowrap',
}

function LayersIcon({ size = 10 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true"
         fill="none" stroke="currentColor" strokeWidth="1.6"
         strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.6 1.6 5 8 8.4 14.4 5 8 1.6Z" />
      <path d="M1.6 8.6 8 12l6.4-3.4" />
    </svg>
  )
}

/** The steps offered when raising the limit, bounded by the ceiling. */
export function rowLimitChoices(current: number, ceiling: number): number[] {
  const out: number[] = []
  for (const step of [current * 2, current * 5, current * 10, ceiling]) {
    const n = Math.min(Math.floor(step), ceiling)
    if (n > current && !out.includes(n)) out.push(n)
  }
  return out
}

export function RowLimitBadge({
  truncated, rowLimit, rowCeiling, rowLimitAuthored, rowLimitFixed,
  onRowLimit, variant = 'inline',
}: RowLimitBadgeProps) {
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const boxRef = useRef<HTMLSpanElement | null>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  if (!truncated) return null

  const limit = Number(rowLimit) > 0 ? Number(rowLimit) : 0
  const ceiling = Number(rowCeiling) > 0 ? Number(rowCeiling) : 0
  const canRaise = !!onRowLimit && !rowLimitFixed && ceiling > limit
  const atCeiling = ceiling > 0 && limit >= ceiling

  const why = rowLimitFixed
    ? 'The author fixed this visual at this many rows.'
    : rowLimitAuthored
      ? "This is the visual's own row limit — there is more data behind it."
      : atCeiling
        ? 'This is the most rows one visual may return here. Narrow the '
          + 'criteria, or open the underlying data, to see the rest.'
        : 'There is more data behind this.'

  const apply = (n: number) => {
    if (!onRowLimit) return
    const bounded = Math.max(1, Math.min(Math.floor(n), ceiling || n))
    setOpen(false)
    setTyped('')
    onRowLimit(bounded)
  }

  const wrapStyle: CSSProperties = variant === 'sticky'
    ? {
        position: 'sticky', top: 0, zIndex: 3, alignSelf: 'flex-start',
        display: 'inline-flex', margin: '2px 0 2px 2px',
        background: 'var(--pg-surface, var(--color-surface-2, #fff))',
      }
    : { position: 'relative', display: 'inline-flex' }

  return (
    <span ref={boxRef} style={wrapStyle}>
      <span
        style={{ ...CHIP, cursor: canRaise ? 'pointer' : 'default' }}
        title={canRaise ? `${why} Click to show more.` : why}
        onClick={canRaise ? () => setOpen((v) => !v) : undefined}
        role={canRaise ? 'button' : undefined}
        tabIndex={canRaise ? 0 : undefined}
        onKeyDown={canRaise
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') setOpen((v) => !v) }
          : undefined}
      >
        <LayersIcon size={10} />
        {limit ? `first ${limit.toLocaleString()} rows` : 'partial result'}
        {canRaise && <span aria-hidden="true">▾</span>}
      </span>
      {open && canRaise && (
        <span
          style={{
            position: 'absolute', top: '100%', right: 0, zIndex: 40,
            marginTop: 4, padding: 8, minWidth: 190,
            display: 'flex', flexDirection: 'column', gap: 6,
            background: 'var(--pg-surface, var(--color-surface-2, #fff))',
            border: '1px solid var(--pg-border, var(--color-border, #d6dae0))',
            borderRadius: 8, boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
            fontSize: 12, color: 'var(--pg-text, var(--color-text, #111827))',
          }}
        >
          <span style={{ color: 'var(--pg-text-secondary, var(--color-text-secondary, #6b7280))' }}>
            Show how many rows?
          </span>
          {rowLimitChoices(limit || 1, ceiling).map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => apply(n)}
              style={{
                border: '1px solid var(--pg-border, var(--color-border, #d6dae0))', borderRadius: 6,
                background: 'transparent', color: 'inherit', cursor: 'pointer',
                padding: '3px 8px', fontSize: 12, textAlign: 'left',
              }}
            >
              {n >= ceiling ? `${n.toLocaleString()} (the maximum)` : n.toLocaleString()}
            </button>
          ))}
          <span style={{ display: 'flex', gap: 4 }}>
            <input
              type="number"
              min={1}
              max={ceiling || undefined}
              value={typed}
              placeholder="or type a number"
              onChange={(e) => setTyped(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return
                const n = parseInt(typed, 10)
                if (Number.isFinite(n) && n > 0) apply(n)
              }}
              style={{
                flex: 1, minWidth: 0, padding: '3px 6px', fontSize: 12,
                border: '1px solid var(--pg-border, var(--color-border, #d6dae0))', borderRadius: 6,
                background: 'transparent', color: 'inherit',
              }}
            />
            <button
              type="button"
              onClick={() => {
                const n = parseInt(typed, 10)
                if (Number.isFinite(n) && n > 0) apply(n)
              }}
              style={{
                border: '1px solid var(--pg-border, var(--color-border, #d6dae0))', borderRadius: 6,
                background: 'transparent', color: 'inherit', cursor: 'pointer',
                padding: '3px 8px', fontSize: 12,
              }}
            >
              Show
            </button>
          </span>
          {ceiling > 0 && (
            <span style={{ color: 'var(--pg-text-secondary, var(--color-text-secondary, #6b7280))', fontSize: 11 }}>
              At most {ceiling.toLocaleString()} rows here.
            </span>
          )}
        </span>
      )}
    </span>
  )
}

export default RowLimitBadge
